import React, { useEffect, useState } from "react";
import matter from "gray-matter";
import ReactMarkdown from "react-markdown";
import { Buffer } from "buffer";
import {Album, IFigure} from '../Album';  
import { Skill, skillsList} from '../Skills'; 

// Ensure Buffer is globally available in the browser
if (typeof window !== "undefined") {
  (window as any).Buffer = Buffer;
}


// Define the type for frontmatter
interface Frontmatter {
  title : string; 
  mediaPath : string; // A Demo media path (can be video (mp4), an album (json) or a webpage (html))
}
 


// Define the type for the markdown content
interface MarkdownData {
  frontmatter: Frontmatter;
  content: string;
  media : React.ReactElement;
}
 
interface IPropsProject {
  filePath : string;
  onAlbumToggle ?: (isOpen: boolean) => void;
}

export default function Project({ filePath, onAlbumToggle } : IPropsProject) {
  // filePath : the path of the markdown
  // State to store the Markdown data
  const [markdownData, setMarkdownData] = useState<MarkdownData | null>(null);
  const [loading, setLoading] = useState(true); 

  useEffect(() => {
    // Fetch the Markdown file
    const fetchMarkdown = async () => {
      try {
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error("Failed to fetch markdown file");
        }
        const text = await response.text(); 
        // Parse the frontmatter and content
        const { data, content } = matter(text);
        const frontmatter = data as Frontmatter;
          
        // Load the media file 
        const mediaResponse = await fetch(frontmatter.mediaPath);
        if (!response.ok) {
          throw new Error("Failed to fetch media file");
        }
 
        let media : React.ReactElement = <></>;

        // Either json / mp4 / html
        if (frontmatter.mediaPath.endsWith(".json")) {
          // json -> Album
          const figures = JSON.parse(await mediaResponse.text()) as IFigure[]; 
          media = <Album figures={figures} onAlbumToggle={onAlbumToggle}/>;
        } else if (frontmatter.mediaPath.endsWith(".mp4")) {
          // mp4 -> Video
          media = <video width="100%" style={{paddingBottom : "1em"}} controls>
              <source src={frontmatter.mediaPath} type="video/mp4"/> 
            </video>;
        } else if (frontmatter.mediaPath.endsWith(".html") || frontmatter.mediaPath.startsWith("https://")) {
          // html -> integrated web page
          media = <iframe style={{paddingBottom : "1em"}} className="slider-media" title={frontmatter.title} src={frontmatter.mediaPath}></iframe>;
        }

        // Update state with parsed data
        setMarkdownData({ frontmatter, content, media });
      } catch (error) {
        console.error("Error loading Markdown file:", error);
      } finally {
        // Ensure loading is set to false after fetching
        setLoading(false);
      }
    };

    fetchMarkdown();
  }, [filePath, onAlbumToggle]); // Ensure the effect runs when the filePath changes

  // Show a loading message while fetching data
  if (loading) { 
    return <article><span aria-busy="true">Loading...</span></article>;
  }

  // Show an error message if no data was loaded
  if (!markdownData) { 
    return <article><p style={{color:"red"}}>Error loading content</p></article>;
  }
  
  return (
    <article> 
      <h3>{markdownData.frontmatter.title}</h3>  
      {markdownData.media}
      <ReactMarkdown 
        components={{
          code({children, className, node, ...rest}) {
            const text = String(children);
            if (text in skillsList)
              return <Skill>{text}</Skill>
            else
              return (<code {...rest} className={className}>
                  {children}
                </code>);
          }    
        }}
      >{markdownData.content}</ReactMarkdown>
    </article>
  );
}; 